import type { Tone } from "@/pages/admin/components/AdminUI";

export interface StatusMeta {
  label: string;
  tone: Tone;
}

/** A single image inside a content record's photo gallery. */
export interface GalleryItem {
  src: string;
  alt: string;
}

const meta = (label: string, tone: Tone): StatusMeta => ({ label, tone });

export function metaFor(map: Record<string, StatusMeta>, key: string | null | undefined): StatusMeta {
  if (!key) return { label: "—", tone: "neutral" };
  return map[key] || { label: key, tone: "neutral" };
}

export function optionsFrom(map: Record<string, StatusMeta>): { value: string; label: string }[] {
  return Object.entries(map).map(([value, m]) => ({ value, label: m.label }));
}

/* ------------------------------ Website Pages ------------------------------- */
export interface WebsitePage {
  id: number;
  title: string;
  slug: string | null;
  page_type: string | null;
  status: string;
  sections: number;
  author: string | null;
  seo_title: string | null;
  seo_description: string | null;
  views: number;
  created_at: string;
  updated_at: string;
}

export const PAGE_STATUS: Record<string, StatusMeta> = {
  published: meta("Published", "primary"),
  review: meta("In Review", "accent"),
  draft: meta("Draft", "neutral"),
  archived: meta("Archived", "neutral"),
};

export const PAGE_TYPES = ["page", "landing", "guide", "tool", "legal", "system"];

/* --------------------------------- Blog ------------------------------------- */
export interface BlogPost {
  id: number;
  title: string;
  slug: string | null;
  category: string | null;
  excerpt: string | null;
  content: string | null;
  author: string | null;
  cover_image: string | null;
  tags: string[] | null;
  gallery: GalleryItem[] | null;
  featured: boolean;
  status: string;
  views: number;
  published_at: string | null;
  scheduled_for: string | null;
  created_at: string;
  updated_at: string;
}

export const BLOG_STATUS: Record<string, StatusMeta> = {
  published: meta("Published", "primary"),
  scheduled: meta("Scheduled", "secondary"),
  review: meta("In Review", "accent"),
  draft: meta("Draft", "neutral"),
  archived: meta("Archived", "neutral"),
};

export const BLOG_CATEGORIES = ["Seasonal", "Family", "Farmers", "Schools", "Food", "Travel", "Guides"];

/* ------------------------------ Farmer Stories ------------------------------ */
export interface FarmerStory {
  id: number;
  title: string;
  farmer_name: string | null;
  farm_name: string | null;
  village: string | null;
  category: string | null;
  excerpt: string | null;
  content: string | null;
  cover_image: string | null;
  read_time: string | null;
  gallery: GalleryItem[] | null;
  featured: boolean;
  status: string;
  published_at: string | null;
  scheduled_for: string | null;
  created_at: string;
  updated_at: string;
}

export const STORY_STATUS: Record<string, StatusMeta> = {
  published: meta("Published", "primary"),
  scheduled: meta("Scheduled", "secondary"),
  review: meta("In Review", "accent"),
  draft: meta("Draft", "neutral"),
  archived: meta("Archived", "neutral"),
};

/* ---------------------------- Story Submissions ----------------------------- */
export interface StorySubmission {
  id: number;
  submitter_name: string;
  email: string;
  phone: string | null;
  village: string | null;
  title: string;
  category: string | null;
  story: string | null;
  cover_image: string | null;
  status: string;
  admin_notes: string | null;
  story_id: number | null;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

export const SUBMISSION_STATUS: Record<string, StatusMeta> = {
  pending: meta("New", "accent"),
  review: meta("Reading", "secondary"),
  approved: meta("Approved", "primary"),
  rejected: meta("Declined", "neutral"),
};

/* ------------------------------ Media Library ------------------------------- */
export interface MediaAsset {
  id: number;
  file_name: string;
  title: string | null;
  asset_type: string;
  folder: string | null;
  url: string | null;
  size_kb: number;
  dimensions: string | null;
  alt_text: string | null;
  uploaded_by: string | null;
  usage_count: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export const MEDIA_TYPE: Record<string, StatusMeta> = {
  image: meta("Image", "primary"),
  video: meta("Video", "accent"),
  document: meta("Document", "secondary"),
};

export const MEDIA_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  archived: meta("Archived", "neutral"),
};

/* ----------------------------- Support Tickets ------------------------------ */
export interface SupportTicket {
  id: number;
  ticket_ref: string;
  subject: string;
  requester_name: string | null;
  requester_email: string | null;
  category: string | null;
  priority: string;
  status: string;
  assigned_to: string | null;
  related_booking: string | null;
  message: string | null;
  created_at: string;
  updated_at: string;
}

export const TICKET_STATUS: Record<string, StatusMeta> = {
  open: meta("Open", "accent"),
  in_progress: meta("In Progress", "secondary"),
  resolved: meta("Resolved", "primary"),
  closed: meta("Closed", "neutral"),
};

export const TICKET_PRIORITY: Record<string, StatusMeta> = {
  high: meta("High", "accent"),
  medium: meta("Medium", "secondary"),
  low: meta("Low", "neutral"),
};

export const TICKET_CATEGORIES = [
  "Booking",
  "Payment",
  "Billing",
  "Feedback",
  "Accessibility",
  "Technical",
  "Sales",
  "Other",
];

export function formatSize(kb: number): string {
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${kb} KB`;
}

/* ---------------------------------- FAQs ------------------------------------ */
export interface Faq {
  id: number;
  question: string;
  answer: string;
  category: string | null;
  scope: string | null;
  sort_order: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export const FAQ_STATUS: Record<string, StatusMeta> = {
  published: meta("Published", "primary"),
  draft: meta("Draft", "neutral"),
  archived: meta("Archived", "neutral"),
};

export const FAQ_SCOPES = ["General", "Booking", "Experiences", "Farms", "Konaseema", "Trip Planning"];
export const FAQ_CATEGORIES = ["General", "Booking", "Payment", "Safety", "Food", "Travel"];

/* ------------------------------ Testimonials -------------------------------- */
export interface Testimonial {
  id: number;
  name: string;
  role: string | null;
  location: string | null;
  quote: string;
  experience: string | null;
  rating: number;
  avatar: string | null;
  featured: boolean;
  sort_order: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export const TESTIMONIAL_STATUS: Record<string, StatusMeta> = {
  published: meta("Published", "primary"),
  draft: meta("Draft", "neutral"),
  archived: meta("Archived", "neutral"),
};

/* ---------------------------------- SEO ------------------------------------- */
export interface SeoSetting {
  id: number;
  page_label: string;
  path: string;
  title: string | null;
  description: string | null;
  keywords: string | null;
  canonical: string | null;
  og_image: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const SEO_STATUS: Record<string, StatusMeta> = {
  published: meta("Active", "primary"),
  draft: meta("Draft", "neutral"),
};

/* -------------------------------- Campaigns --------------------------------- */
export interface Campaign {
  id: number;
  name: string;
  channel: string;
  subject: string | null;
  audience: string | null;
  status: string;
  scheduled_at: string | null;
  sent_count: number;
  open_rate: number;
  click_rate: number;
  created_at: string;
  updated_at: string;
}

export const CAMPAIGN_STATUS: Record<string, StatusMeta> = {
  draft: meta("Draft", "neutral"),
  scheduled: meta("Scheduled", "secondary"),
  sending: meta("Sending", "accent"),
  sent: meta("Sent", "primary"),
  paused: meta("Paused", "accent"),
};

export const CAMPAIGN_CHANNELS: Record<string, StatusMeta> = {
  email: meta("Email", "primary"),
  sms: meta("SMS", "secondary"),
  whatsapp: meta("WhatsApp", "accent"),
};

export const CAMPAIGN_AUDIENCES = [
  "All contacts",
  "Past visitors",
  "Families",
  "Schools & teachers",
  "Corporate contacts",
  "Subscribers",
];

/* ------------------------------ Notifications ------------------------------- */
export interface AdminNotification {
  id: number;
  title: string;
  message: string | null;
  type: string;
  audience: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export const NOTIFICATION_STATUS: Record<string, StatusMeta> = {
  draft: meta("Draft", "neutral"),
  sent: meta("Sent", "primary"),
};

export const NOTIFICATION_TYPE: Record<string, StatusMeta> = {
  info: meta("Info", "secondary"),
  promo: meta("Promotion", "primary"),
  alert: meta("Alert", "accent"),
  system: meta("System", "neutral"),
};

export const NOTIFICATION_AUDIENCES = ["all", "visitors", "farmers", "admins"];

/* --------------------------- Outbound messages ------------------------------ */
export interface OutboundMessage {
  id: number;
  channel: string;
  recipient: string;
  subject: string | null;
  body: string | null;
  status: string;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export const MESSAGE_STATUS: Record<string, StatusMeta> = {
  sent: meta("Sent", "primary"),
  delivered: meta("Delivered", "primary"),
  queued: meta("Queued", "secondary"),
  received: meta("Received", "secondary"),
  failed: meta("Failed", "accent"),
};